import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { ArrowRight, Hand } from "lucide-react";
import { EFT_EXERCISES } from "@/data/eft-flashcards";
import { EftSessionHistory } from "@/components/eft/EftSessionHistory";

export const Route = createFileRoute("/eft-flashcards")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "EFT Tapping Flashcards — Guided Tapping Exercises · Recovery Trauma™" },
      {
        name: "description",
        content:
          "Guided EFT tapping flashcards for morning dread, Sunday scaries, overwhelm, shame, anger and more. One card at a time, with a before-and-after intensity tracker.",
      },
      { property: "og:title", content: "EFT Tapping Flashcards — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Short guided tapping rounds, one card at a time, with your own shift tracker.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/eft-flashcards" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/eft-flashcards" }],
  }),
  component: withMemberGate(EftFlashcardsPage, "EFT Tapping Flashcards"),
});

function EftFlashcardsPage() {
  return (
    <main className="mx-auto max-w-3xl px-4 py-10 pb-24">
      <header className="text-center">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
          <Hand className="h-5 w-5 text-foreground" aria-hidden />
        </span>
        <h1 className="mt-4 font-display text-3xl leading-tight text-foreground sm:text-4xl">
          EFT Tapping Flashcards
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
          Short guided tapping rounds for the moments that catch you off guard. One card at a
          time, at your pace — you can stop whenever you want.
        </p>
      </header>

      <section aria-labelledby="eft-exercises-heading" className="mt-10">
        <h2 id="eft-exercises-heading" className="font-display text-lg text-foreground">
          Choose an exercise
        </h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {EFT_EXERCISES.map((ex) => (
            <li key={ex.slug}>
              <Link
                to="/eft-flashcards/$slug"
                params={{ slug: ex.slug }}
                className="group flex h-full flex-col rounded-2xl border border-border/70 bg-card p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-[var(--shadow-gold)]"
              >
                <span className="text-2xl" aria-hidden>
                  {ex.emoji}
                </span>
                <span className="mt-2 font-display text-base text-foreground">{ex.title}</span>
                <span className="mt-1 flex-1 text-sm leading-relaxed text-muted-foreground">
                  {ex.description}
                </span>
                <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--gold-deep)]">
                  Start
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="eft-tracker-heading" className="mt-12">
        <h2 id="eft-tracker-heading" className="font-display text-lg text-foreground">
          Your shift tracker
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Private to you — every saved session, with how the intensity moved.
        </p>
        <div className="mt-4">
          <EftSessionHistory />
        </div>
      </section>

      <p className="mt-12 text-center text-xs leading-relaxed text-muted-foreground">
        Tapping is an educational self-help practice, not therapy, medical treatment or crisis
        support. If you are in crisis, please reach out to local emergency services.
      </p>
    </main>
  );
}
